import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Plus, Edit2, Trash2, Download, FileText, X, Eye } from 'lucide-react';
import { reportsAPI, usersAPI, nasAPI, profilesAPI, tenantsAPI, radiusServersAPI } from '../api';
import { Card, Button, Table, Modal, Input, Select, PageHeader, Alert, Empty, useSort } from '../components/UI';
import { useTenant } from '../TenantContext';
import { useAuth } from '../context/AuthContext';
import { exportRows, exportCombinedTrafficConcurrentPDF } from '../utils/reportExport';

// Preview tabel dibatasi biar gak nge-lag kalau hasilnya ribuan baris —
// export tetap pakai SEMUA baris yang balik dari backend, cuma preview-nya aja yang dipotong.
const PREVIEW_ROW_LIMIT = 200;

const DATASETS = [
  { value: 'histogram',  label: 'Traffic (Timeseries)',           desc: 'Upload/download per bucket waktu — cocok buat grafik traffic' },
  { value: 'concurrent', label: 'Concurrent Users (Timeseries)',  desc: 'Jumlah user/sesi aktif per bucket waktu' },
  { value: 'combined',   label: 'Traffic + Concurrent (Gabungan)',desc: 'Traffic & Concurrent Users dijalanin bareng, divisualisasi jadi 1 PDF (chart ditumpuk)' },
  { value: 'history',    label: 'Raw Data (Tabel)',               desc: 'Data mentah per sesi, apa adanya (bukan time-bucketed)' },
];

const GROUP_BY_OPTIONS = [
  { value: 'username',         label: 'Username' },
  { value: 'realm',            label: 'Realm' },
  { value: 'nasipaddress',     label: 'NAS IP' },
  { value: 'server_name',      label: 'Server Name' },
  { value: 'groupname',        label: 'Profile' },
  { value: 'callingstationid', label: 'MAC Address' },
];

const PERIODS = [
  { value: '1h',    label: '1 Jam Terakhir' },
  { value: '24h',   label: '24 Jam Terakhir' },
  { value: '7d',    label: '7 Hari Terakhir' },
  { value: '30d',   label: '30 Hari Terakhir' },
  { value: 'today', label: 'Hari Ini' },
  { value: 'month', label: 'Bulan Ini' },
  { value: 'custom',label: 'Custom (from – to)' },
];

const GRANULARITIES = [
  { value: '',      label: 'Otomatis' },
  { value: '5m',    label: '5 Menit' },
  { value: 'hour',  label: 'Per Jam' },
  { value: 'day',   label: 'Per Hari' },
  { value: 'week',  label: 'Per Minggu' },
];

const FORMATS = [
  { value: 'csv',  label: 'CSV' },
  { value: 'json', label: 'JSON' },
  { value: 'xml',  label: 'XML' },
  { value: 'xlsx', label: 'Excel (XLSX)' },
  { value: 'pdf',  label: 'PDF (tabel)' },
];

const DATASET_BASE_COLUMNS = {
  histogram:  ['bucket', 'upload_bytes', 'download_bytes'],
  concurrent: ['bucket', 'active_users', 'active_sessions'],
};

// Dataset "history" (Raw Data) punya DUA mode kolom output yang beda total di
// backend (lihat ClickhouseAnalyticsHelper#history_rows di API):
//   - group_by KOSONG -> mode raw per-sesi, semua kolom sesi mentah tersedia.
//   - group_by DIISI  -> mode grouped/agregat, kolom per-sesi (radacctid,
//     server_name, acctterminatecause, framedipaddress, acctstarttime,
//     acctstoptime, dst) HILANG, diganti kolom group_by itu sendiri +
//     ringkasan agregat (session_count, total_bytes, min/max time, dst).
// Field Mapping harus nawarin kolom yang sesuai mode ini — bukan daftar
// statis — biar user gak accidental milih kolom yang bakal selalu null
// (kasus "test223": group_by realm+username tapi fields masih nunjuk ke
// radacctid/acctstarttime/dll yang gak ada di mode grouped).
const HISTORY_RAW_FIELDS = [
  'radacctid', 'server_name', 'username', 'groupname', 'realm', 'nasipaddress',
  'callingstationid', 'calledstationid', 'acctinputoctets', 'acctoutputoctets',
  'total_bytes', 'acctterminatecause', 'framedipaddress', 'acctstarttime', 'acctstoptime',
];
const HISTORY_GROUPED_EXTRA_FIELDS = [
  'session_count', 'acctinputoctets', 'acctoutputoctets', 'total_bytes',
  'min_acctstarttime', 'max_acctstoptime',
];

const EMPTY_FORM = {
  name: '', dataset: 'histogram', group_by: [], where: {}, fields: [],
  period: '30d', from: '', to: '', granularity: '',
};

function availableColumns(form) {
  if (form.dataset === 'history') {
    // Mode grouped: cuma kolom group_by + ringkasan agregat yang beneran ada.
    // Mode raw (group_by kosong): semua kolom sesi mentah.
    return form.group_by.length
      ? [...new Set([...form.group_by, ...HISTORY_GROUPED_EXTRA_FIELDS])]
      : [...HISTORY_RAW_FIELDS];
  }
  return [...new Set([...(DATASET_BASE_COLUMNS[form.dataset] || []), ...form.group_by])];
}


// Kolom-kolom yang secara logis "ikut" realm — kalau salah satu dipilih, realm otomatis ikut aktif
// (sama seperti behaviour Group By di menu Analytics)
const REALM_FOLLOWERS = ['username', 'nasipaddress', 'groupname', 'callingstationid'];

const computeGroupBy = (current, toggled) => {
  const isActive = current.includes(toggled);
  let next = isActive ? current.filter(x => x !== toggled) : [...current, toggled];
  if (next.some(v => REALM_FOLLOWERS.includes(v)) && !next.includes('realm')) {
    next = ['realm', ...next];
  }
  return next;
};

// Field Mapping/Alias bisa ganti nama kolom mentah (mis. 'nasipaddress' jadi
// 'nas'). Biar fitur facet chart di reportExport.js gak nebak-nebak nama
// kolom dari isi rows, kita tentuin di sini kolom mana yang dipakai buat
// facet dan nama akhirnya (setelah alias, kalau ada).
//
// Facet dipilih generik dari dimensi group_by yang paling spesifik — bukan
// cuma NAS: kalau group_by-nya ['realm', 'username'], facet-nya per
// username; kalau ['realm', 'nasipaddress'], facet-nya per NAS; kalau cuma
// ['realm'] aja, facet-nya per realm. Elemen terakhir di group_by dipakai
// karena 'realm' selalu ditaruh di depan waktu auto-follow (lihat
// computeGroupBy), jadi elemen terakhir = dimensi paling spesifik yang
// user pilih.
const GROUP_BY_LABELS = Object.fromEntries(GROUP_BY_OPTIONS.map(g => [g.value, g.label]));

const resolveFacetInfo = (groupBy, fields) => {
  if (!groupBy?.length) return null;
  const raw = groupBy[groupBy.length - 1];
  const mapped = (fields || []).find(f => f.source === raw && f.alias);
  return { key: mapped ? mapped.alias : raw, label: GROUP_BY_LABELS[raw] || raw };
};

// ── Searchable dropdown (sama seperti di menu Analytics) ──────────────────────
function SearchSelect({ label, value, onChange, options, placeholder, allowClear = true }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [pos, setPos] = useState({ top: 0, left: 0, width: 180 });
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const openDropdown = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + window.scrollY + 4, left: r.left + window.scrollX, width: Math.max(r.width, 220) });
    }
    setOpen(v => !v);
    setQ('');
  };

  const filtered = options.filter(o => !q || o.toLowerCase().includes(q.toLowerCase())).slice(0, 50);
  const display = value || placeholder || '—';

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, fontWeight: 600 }}>{label}</div>
      <button type="button" ref={btnRef} onClick={openDropdown}
        style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '7px 12px', cursor: 'pointer', color: value ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: 13, minWidth: 180, width: '100%', justifyContent: 'space-between' }}>
        <span className={value ? 'mono' : ''} style={{ fontSize: value ? 12 : 13 }}>{display}</span>
        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>▾</span>
      </button>
      {open && ReactDOM.createPortal(
        <div ref={popRef} style={{ position: 'absolute', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)', overflow: 'hidden' }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Cari…"
              style={{ width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '5px 8px', fontSize: 12, color: 'var(--text-primary)', outline: 'none' }} />
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {allowClear && (
              <button type="button" onClick={() => { onChange(''); setOpen(false); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                — Semua
              </button>
            )}
            {filtered.map(o => (
              <button type="button" key={o} onClick={() => { onChange(o); setOpen(false); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', background: value === o ? 'rgba(59,130,246,0.12)' : 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: value === o ? 'var(--accent-blue)' : 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}
                onMouseEnter={e => { if (value !== o) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                onMouseLeave={e => { if (value !== o) e.currentTarget.style.background = 'none'; }}>
                {o}
              </button>
            ))}
            {filtered.length === 0 && <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-muted)' }}>Tidak ada hasil</div>}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Debounced text filter (untuk filter yang gak punya daftar opsi, mis. Called Station) ──
function TextFilterInput({ label, value, onChange, placeholder }) {
  const [local, setLocal] = useState(value || '');

  useEffect(() => { setLocal(value || ''); }, [value]);

  useEffect(() => {
    const t = setTimeout(() => { if (local !== (value || '')) onChange(local); }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]);

  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4, fontWeight: 600 }}>{label}</div>
      <input
        type="text"
        value={local}
        onChange={e => setLocal(e.target.value)}
        placeholder={placeholder}
        style={{ width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '7px 10px', color: 'var(--text-primary)', fontSize: 13, outline: 'none', minWidth: 180, fontFamily: 'var(--font-mono)' }}
      />
    </div>
  );
}

export default function Reports() {
  const { selectedTenant, currentTenant } = useTenant();
  const { user } = useAuth();
  const isOperator = user?.role === 'operator';
  const isUser     = user?.role === 'user';
  const restricted = isOperator || isUser;

  const [templates, setTemplates] = useState([]);
  const { sort, onSortChange } = useSort();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [warning, setWarning] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingRowId, setGeneratingRowId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Hasil generate — backend sekarang balikin JSON polos ({ title, dataset, total, rows }),
  // jadi kita render dulu di modal preview, baru user pilih mau di-export ke format apa.
  const [result, setResult] = useState(null);
  const [resultOpen, setResultOpen] = useState(false);
  const [exportingFormat, setExportingFormat] = useState(null);

  // Opsi dropdown filter — sama seperti FilterBar di menu Analytics
  const [userList, setUserList]       = useState([]);
  const [nasList, setNasList]         = useState([]);
  const [profileList, setProfileList] = useState([]);
  const [realmList, setRealmList]     = useState([]);
  const [serverList, setServerList]   = useState([]);

  useEffect(() => {
    if (!modalOpen || !selectedTenant) return;
    usersAPI.list(selectedTenant, { per_page: 200 }).then(r => setUserList((r.users || []).map(u => u.username))).catch(() => {});
    nasAPI.list(selectedTenant, { per_page: 200 }).then(r => setNasList((r.nas_devices || []).map(n => n.nasname))).catch(() => {});
    profilesAPI.list(selectedTenant, { per_page: 200 }).then(r => setProfileList((r.profiles || []).map(p => p.name))).catch(() => {});
  }, [modalOpen, selectedTenant]);

  useEffect(() => {
    if (!modalOpen) return;
    if (!restricted) {
      // superadmin/admin: semua realm & server untuk dropdown
      tenantsAPI.list({ per_page: 200 }).then(r => setRealmList([...new Set((r.tenants || []).map(t => t.realm).filter(Boolean))])).catch(() => {});
      radiusServersAPI.list({ per_page: 200 }).then(r => setServerList((r.radius_servers || []).map(s => s.name).filter(Boolean))).catch(() => {});
    } else if (isOperator) {
      // operator: hanya realm dari tenant_ids mereka
      const tenantIds = user?.tenant_ids || [];
      tenantsAPI.list({ per_page: 200 }).then(r => {
        const tenants = r.tenants || [];
        const myRealms = tenants
          .filter(t => tenantIds.includes(t.id) || tenantIds.includes(String(t.id)))
          .map(t => t.realm)
          .filter(Boolean);
        setRealmList([...new Set(myRealms)]);
      }).catch(() => {});
    } else if (isUser) {
      // user: cuma realm dari tenant dia sendiri (dari TenantContext, gak perlu fetch /tenants lagi).
      setRealmList(currentTenant?.realm ? [currentTenant.realm] : []);
    }
  }, [modalOpen, restricted, isOperator, isUser, user, currentTenant]);

  // Group By options: semua role lihat, tapi operator/user tidak lihat server_name (sama seperti Analytics)
  const visibleGroupByOptions = GROUP_BY_OPTIONS.filter(g => !restricted || g.value !== 'server_name');

  const load = useCallback(async () => {
    setLoading(true);
    try { setTemplates(await reportsAPI.templates.list()); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!success && !error && !warning) return;
    const t = setTimeout(() => { setSuccess(''); setError(''); setWarning(''); }, 6000);
    return () => clearTimeout(t);
  }, [success, error, warning]);

  const sf = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openCreate = () => {
    // Role user WAJIB punya where.realm terisi (backend nolak kalau kosong) —
    // langsung prefill dari realm tenant dia sendiri.
    const where = isUser && currentTenant?.realm ? { realm: currentTenant.realm } : {};
    setForm({ ...EMPTY_FORM, where });
    setEditingId(null);
    setModalOpen(true);
  };

  const openEdit = (t) => {
    const cfg = t.config || {};
    const where = cfg.where || {};
    setForm({
      name: t.name, dataset: t.dataset,
      group_by: cfg.group_by || [],
      // Kalau template lama belum punya realm (dibuat sebelum aturan ini ada), isi otomatis.
      where: (isUser && !where.realm && currentTenant?.realm) ? { ...where, realm: currentTenant.realm } : where,
      fields: cfg.fields || [],
      period: cfg.period || '30d', from: cfg.from || '', to: cfg.to || '',
      granularity: cfg.granularity || '',
    });
    setEditingId(t.id);
    setModalOpen(true);
  };

  const toggleGroupBy = (col) => {
    setForm(f => {
      const group_by = computeGroupBy(f.group_by, col);

      // `fields` itu whitelist buat SEMUA dataset (history/histogram/
      // concurrent): kalau user udah pakai custom mapping (fields gak
      // kosong), kolom yang GAK ada barisnya di situ gak bakal keluar di
      // hasil sama sekali — bukan cuma warning, beneran hilang. Jadi begitu
      // ada kolom group_by baru yang aktif (mis. nasipaddress), auto-tambahin
      // baris mapping-nya (source=alias=nama kolom) SUPAYA GAK HILANG DIAM-
      // DIAM, tapi cuma kalau kolomnya belum ada baris mapping sama sekali.
      // Baris yang UDAH ADA (termasuk yang sekarang gak valid, mis. username
      // abis di-uncheck) TETAP DIBIARKAN — dihapus manual lewat "Bersihkan
      // tidak valid" atau tombol X, bukan otomatis.
      if (f.fields.length === 0) return { ...f, group_by };

      const added = group_by.filter(c => !f.group_by.includes(c));
      const missing = added.filter(c => !f.fields.some(row => row.source === c));
      if (missing.length === 0) return { ...f, group_by };

      return { ...f, group_by, fields: [...f.fields, ...missing.map(c => ({ source: c, alias: c }))] };
    });
  };

  // Buang manual semua baris field mapping yang source-nya udah gak valid
  // buat dataset+group_by SEKARANG — dipakai user kalau emang mau beres-beres
  // (bukan otomatis, biar toggle Group By bolak-balik tetap aman).
  const cleanInvalidFields = () => setForm(f => ({
    ...f, fields: f.fields.filter(row => !row.source || availableColumns(f).includes(row.source)),
  }));

  // Ganti dataset -> kolom mentah yang tersedia ikut berubah (mis. dari
  // 'history' ke 'histogram'), jadi Field Mapping lama (source-nya nama
  // kolom dataset sebelumnya) bisa nunjuk ke kolom yang gak ada lagi ->
  // hasil export jadi kosong/aneh. Reset otomatis field mapping-nya di sini
  // supaya user gak perlu ingat buat bersihin manual.
  const changeDataset = (value) => {
    setForm(f => (f.dataset === value ? f : { ...f, dataset: value, fields: [] }));
  };

  const setWhereField = (col, val) => {
    setForm(f => {
      const where = { ...f.where };
      if (val) where[col] = val; else delete where[col];
      return { ...f, where };
    });
  };

  const addFieldRow = () => setForm(f => ({ ...f, fields: [...f.fields, { source: '', alias: '' }] }));
  const removeFieldRow = (i) => setForm(f => ({ ...f, fields: f.fields.filter((_, idx) => idx !== i) }));
  const updateFieldRow = (i, key, val) => setForm(f => ({
    ...f, fields: f.fields.map((row, idx) => idx === i ? { ...row, [key]: val } : row),
  }));
  const fillAllColumns = () => setForm(f => ({
    ...f, fields: availableColumns(f).map(c => ({ source: c, alias: c })),
  }));

  const buildPayload = () => ({
    dataset: form.dataset,
    group_by: form.group_by,
    where: form.where,
    fields: form.fields.filter(f => f.source),
    period: form.period,
    from: form.period === 'custom' ? form.from : undefined,
    to: form.period === 'custom' ? form.to : undefined,
    granularity: form.granularity || undefined,
  });

  // Role 'user' WAJIB isi where.realm dengan salah satu realm tenant-nya sendiri.
  const validateRealmRequired = () => {
    if (isUser && !form.where.realm) {
      setError('Wajib isi filter Realm dengan realm tenant Anda.');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Nama template wajib diisi.'); return; }
    if (!validateRealmRequired()) return;
    setSaving(true);
    try {
      const payload = { name: form.name, ...buildPayload() };
      const res = editingId
        ? await reportsAPI.templates.update(editingId, payload)
        : await reportsAPI.templates.create(payload);
      setSuccess(`Template "${form.name}" tersimpan.`);
      setWarning(res?.field_warnings?.message || '');
      setModalOpen(false);
      load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleGenerateInline = async () => {
    if (!validateRealmRequired()) return;
    setGenerating(true);
    try {
      const res = await reportsAPI.generate(buildPayload());
      setResult({
        ...res, defaultFormat: 'csv', name: form.name || res.title || 'Report',
        facet: resolveFacetInfo(form.group_by, form.fields),
      });
      setWarning(res?.field_warnings?.message || '');
      setResultOpen(true);
    } catch (e) { setError(e.message); }
    finally { setGenerating(false); }
  };

  const handleGenerateTemplate = async (t) => {
    setGeneratingRowId(t.id);
    try {
      const res = await reportsAPI.generate({ template_id: t.id });
      setResult({
        ...res, defaultFormat: 'csv', name: t.name || res.title || 'Report',
        facet: resolveFacetInfo(t.config?.group_by, t.config?.fields),
      });
      setWarning(res?.field_warnings?.message || '');
      setResultOpen(true);
    } catch (e) { setError(e.message); }
    finally { setGeneratingRowId(null); }
  };

  const handleExport = async (fmt) => {
    if (!result?.rows) return;
    setExportingFormat(fmt);
    try {
      await exportRows(result.rows, fmt, result.name, result.dataset, result.facet?.key, result.facet?.label);
      setSuccess(`Report berhasil di-export sebagai ${fmt.toUpperCase()}.`);
    } catch (e) { setError(e.message); }
    finally { setExportingFormat(null); }
  };

  // dataset "combined" udah balik { traffic: { rows }, concurrent: { rows } }
  // dari SATU request /reports/generate (lihat ClickhouseAnalyticsHelper /
  // ReportsAPI di backend) — tinggal divisualisasi jadi PDF, gak perlu
  // request tambahan lagi.
  const handleExportCombined = async () => {
    if (!result?.traffic || !result?.concurrent) return;
    setExportingFormat('pdf-combined');
    try {
      await exportCombinedTrafficConcurrentPDF(
        result.traffic.rows, result.concurrent.rows, result.name, result.facet?.key, result.facet?.label
      );
      setSuccess('Report gabungan Traffic + Concurrent berhasil di-export sebagai PDF.');
    } catch (e) { setError(e.message); }
    finally { setExportingFormat(null); }
  };

  const columnsFromRows = (rows) => {
    const first = rows?.[0];
    return first ? Object.keys(first).map(k => ({ key: k, label: k })) : [];
  };
  const previewColumns = useMemo(() => columnsFromRows(result?.rows), [result]);
  const trafficPreviewColumns = useMemo(() => columnsFromRows(result?.traffic?.rows), [result]);
  const concurrentPreviewColumns = useMemo(() => columnsFromRows(result?.concurrent?.rows), [result]);

  // Dipakai buat nge-highlight row Field Mapping yang source-nya udah gak
  // valid buat kombinasi dataset+group_by SEKARANG (mis. abis toggle Group
  // By). Cuma buat tanda visual — gak pernah menghapus row-nya sendiri,
  // biar user bebas toggle Group By bolak-balik tanpa kehilangan mapping
  // yang udah diketik (mis. alias "mac-address" buat callingstationid).
  const currentValidColumns = useMemo(
    () => new Set(availableColumns(form)),
    [form.dataset, form.group_by]
  );

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await reportsAPI.templates.delete(deleteConfirm.id);
      setSuccess('Template dihapus.');
      setDeleteConfirm(null);
      load();
    } catch (e) { setError(e.message); }
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true, render: v => <span style={{ fontWeight: 500 }}>{v}</span> },
    { key: 'dataset', label: 'Dataset', sortable: true, render: v => <span style={{ fontSize: 11, padding: '2px 6px', background: 'var(--bg-input)', borderRadius: 4 }}>{DATASETS.find(d => d.value === v)?.label || v}</span> },
    { key: 'group_by_display', label: 'Group By', render: (_, t) => (t.config?.group_by || []).length ? (t.config.group_by.join(', ')) : <span style={{ color: 'var(--text-muted)' }}>—</span> },
    {
      key: 'id', label: 'Actions', render: (_, t) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="sm" variant="ghost" loading={generatingRowId === t.id} onClick={() => handleGenerateTemplate(t)}>
            <Eye size={12} /> Generate
          </Button>
          <Button size="sm" variant="ghost" onClick={() => openEdit(t)}><Edit2 size={12} /></Button>
          <Button size="sm" variant="ghost" onClick={() => setDeleteConfirm(t)}><Trash2 size={12} color="var(--accent-red)" /></Button>
        </div>
      )
    },
  ];

  // reportsAPI.templates.list() gak dipaginasi/gak terima query params (fetch
  // semua sekaligus) — jadi sort dilakukan di frontend terhadap hasil fetch.
  const sortedTemplates = useMemo(() => {
    if (!sort) return templates;
    const { key, dir } = sort;
    const arr = [...templates].sort((a, b) => {
      const av = a[key], bv = b[key];
      if (av === bv) return 0;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      return av > bv ? 1 : -1;
    });
    return dir === 'desc' ? arr.reverse() : arr;
  }, [templates, sort]);

  return (
    <div className="animate-in">
      <PageHeader title="Reports" subtitle="Custom report builder — pilih dataset, group by, filter, alias kolom, lalu export"
        actions={<Button onClick={openCreate}><Plus size={14} /> Template Baru</Button>} />

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {warning && <Alert type="warning" message={warning} onClose={() => setWarning('')} />}

      <Card style={{ padding: 0 }}>
        {templates.length === 0 && !loading ? (
          <Empty message="Belum ada report template. Bikin dulu buat mulai export." icon={FileText} />
        ) : (
          <Table columns={columns} data={sortedTemplates} loading={loading} sort={sort} onSortChange={onSortChange} emptyMessage="Belum ada template" />
        )}
      </Card>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editingId ? 'Edit Report Template' : 'Report Template Baru'} width={720}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Input label="Nama Template" value={form.name} onChange={e => sf('name', e.target.value)} placeholder="Traffic per MAC 30 hari" />

          <div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500, marginBottom: 6 }}>Dataset</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {DATASETS.map(d => (
                <button key={d.value} type="button" onClick={() => changeDataset(d.value)}
                  style={{
                    textAlign: 'left', padding: 10, borderRadius: 'var(--radius)', cursor: 'pointer',
                    border: `1px solid ${form.dataset === d.value ? 'var(--accent-blue)' : 'var(--border)'}`,
                    background: form.dataset === d.value ? 'rgba(59,130,246,0.08)' : 'var(--bg-input)',
                  }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{d.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{d.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500, marginBottom: 6 }}>Group By</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {visibleGroupByOptions.map(g => {
                const active    = form.group_by.includes(g.value);
                const autoRealm = g.value === 'realm' && form.group_by.some(v => REALM_FOLLOWERS.includes(v));
                return (
                  <button key={g.value} type="button" onClick={() => toggleGroupBy(g.value)}
                    title={autoRealm ? 'Realm otomatis aktif saat username/NAS/profile/MAC dipilih' : undefined}
                    style={{
                      fontSize: 12, padding: '5px 10px', borderRadius: 999, cursor: 'pointer',
                      border: `1px solid ${active ? 'var(--accent-blue)' : 'var(--border)'}`,
                      background: active ? 'var(--accent-blue)' : 'var(--bg-input)',
                      color: active ? '#fff' : 'var(--text-secondary)',
                    }}>
                    {g.label}{autoRealm ? ' *' : ''}
                  </button>
                );
              })}
            </div>
            {form.group_by.some(v => REALM_FOLLOWERS.includes(v)) && (
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
                * Realm otomatis ikut aktif
              </div>
            )}
          </div>

          <div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500, marginBottom: 6 }}>Filter (opsional)</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {!restricted && (
                <SearchSelect
                  label="Server" value={form.where.server_name || ''}
                  onChange={v => setWhereField('server_name', v)}
                  options={serverList} placeholder="Semua server"
                />
              )}
              <SearchSelect
                label="Realm"
                value={form.where.realm || ''}
                onChange={v => setWhereField('realm', v)}
                options={realmList}
                placeholder={isUser ? 'Pilih realm' : 'Semua realm'}
                allowClear={!restricted}
              />
              <SearchSelect label="Username" value={form.where.username || ''} onChange={v => setWhereField('username', v)} options={userList} placeholder="Semua user" />
              <SearchSelect label="NAS IP" value={form.where.nasipaddress || ''} onChange={v => setWhereField('nasipaddress', v)} options={nasList} placeholder="Semua NAS" />
              <SearchSelect label="Profile" value={form.where.groupname || ''} onChange={v => setWhereField('groupname', v)} options={profileList} placeholder="Semua profile" />
              <TextFilterInput label="MAC Address" value={form.where.callingstationid || ''} onChange={v => setWhereField('callingstationid', v)} placeholder="AA:BB:CC:DD:EE:FF" />
            </div>
          </div>

          {form.dataset === 'combined' ? (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 10 }}>
              Mode Traffic + Concurrent selalu pakai kolom mentah apa adanya (gak ada Field Mapping/alias) — hasilnya divisualisasi jadi PDF (chart Traffic di atas, Concurrent Users di bawah), bukan tabel.
            </div>
          ) : (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>Field Mapping / Alias (opsional)</span>
              <div style={{ display: 'flex', gap: 6 }}>
                {form.fields.some(row => row.source && !currentValidColumns.has(row.source)) && (
                  <button type="button" onClick={cleanInvalidFields}
                    style={{ fontSize: 12, padding: '5px 10px', borderRadius: 'var(--radius)', cursor: 'pointer', border: '1px solid var(--accent-orange)', background: 'transparent', color: 'var(--accent-orange)' }}>
                    Bersihkan tidak valid
                  </button>
                )}
                <Button size="sm" variant="ghost" onClick={fillAllColumns}>Isi semua kolom</Button>
                <Button size="sm" variant="ghost" onClick={addFieldRow}><Plus size={12} /> Tambah</Button>
              </div>
            </div>
            {form.dataset === 'history' && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
                {form.group_by.length
                  ? `Mode grouped (per ${form.group_by.join(' + ')}) — kolom per-sesi mentah (radacctid, acctstarttime, dst) gak tersedia, diganti ringkasan agregat (session_count, total_bytes, min/max time).`
                  : 'Mode raw per-sesi — semua kolom sesi mentah tersedia. Aktifkan Group By buat lihat mode agregat.'}
              </div>
            )}
            {form.fields.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Kosong = semua kolom hasil query dipakai apa adanya. Klik "Isi semua kolom" buat mulai, lalu ganti nama alias-nya.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {form.fields.map((row, i) => {
                  const invalid = row.source && !currentValidColumns.has(row.source);
                  return (
                    <div key={i}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input list="report-columns" value={row.source} onChange={e => updateFieldRow(i, 'source', e.target.value)}
                          placeholder="nama kolom asli, mis. calledstationid"
                          style={{ flex: 1, background: 'var(--bg-input)', border: `1px solid ${invalid ? 'var(--accent-orange)' : 'var(--border)'}`, borderRadius: 'var(--radius)', padding: '7px 10px', color: 'var(--text-primary)', fontSize: 12, fontFamily: 'var(--font-mono)' }} />
                        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>→</span>
                        <input value={row.alias} onChange={e => updateFieldRow(i, 'alias', e.target.value)}
                          placeholder="nama alias, mis. mac-address"
                          style={{ flex: 1, background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '7px 10px', color: 'var(--text-primary)', fontSize: 12, fontFamily: 'var(--font-mono)' }} />
                        <button type="button" onClick={() => removeFieldRow(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-red)' }}><X size={14} /></button>
                      </div>
                      {invalid && (
                        <div style={{ fontSize: 10, color: 'var(--accent-orange)', marginTop: 2, marginLeft: 2 }}>
                          "{row.source}" gak ada di mode {form.group_by.length ? 'grouped' : 'raw'} saat ini — hasilnya bakal null kalau di-generate begini. Ganti group_by atau nama kolomnya (mapping tetap tersimpan, gak dihapus).
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <datalist id="report-columns">
              {availableColumns(form).map(c => <option key={c} value={c} />)}
            </datalist>
          </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: form.period === 'custom' ? 'repeat(2, 1fr)' : '1fr 1fr', gap: 8 }}>
            <Select label="Rentang Waktu" value={form.period} onChange={e => sf('period', e.target.value)}>
              {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </Select>
            {form.dataset !== 'history' && (
              <Select label="Granularity" value={form.granularity} onChange={e => sf('granularity', e.target.value)}>
                {GRANULARITIES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
              </Select>
            )}
          </div>
          {form.period === 'custom' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              <Input label="From" type="datetime-local" value={form.from} onChange={e => sf('from', e.target.value)} />
              <Input label="To" type="datetime-local" value={form.to} onChange={e => sf('to', e.target.value)} />
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid var(--border)' }}>
            <Button variant="ghost" onClick={handleGenerateInline} loading={generating}>
              <Eye size={14} /> Generate Sekarang
            </Button>
            <Button onClick={handleSave} loading={saving}>{editingId ? 'Simpan Perubahan' : 'Simpan Template'}</Button>
          </div>
        </div>
      </Modal>

      <Modal open={resultOpen} onClose={() => setResultOpen(false)} title={result?.title || 'Hasil Report'} width={960}>
        {result && (result.dataset === 'combined' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Dataset <strong style={{ color: 'var(--text-secondary)' }}>Traffic + Concurrent</strong> ·
                {' '}Traffic {result.traffic?.total ?? 0} baris · Concurrent {result.concurrent?.total ?? 0} baris
              </div>
              <Button size="sm" loading={exportingFormat === 'pdf-combined'} onClick={handleExportCombined}>
                <Download size={12} /> Export PDF (Traffic + Concurrent)
              </Button>
            </div>

            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Traffic</div>
              {result.traffic?.rows?.length ? (
                <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', maxHeight: 220, overflow: 'auto' }}>
                  <Table columns={trafficPreviewColumns} data={result.traffic.rows.slice(0, PREVIEW_ROW_LIMIT)} emptyMessage="Tidak ada data" />
                </div>
              ) : (
                <Empty message="Tidak ada data traffic untuk filter/periode ini." icon={FileText} />
              )}
            </div>

            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Concurrent Users</div>
              {result.concurrent?.rows?.length ? (
                <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', maxHeight: 220, overflow: 'auto' }}>
                  <Table columns={concurrentPreviewColumns} data={result.concurrent.rows.slice(0, PREVIEW_ROW_LIMIT)} emptyMessage="Tidak ada data" />
                </div>
              ) : (
                <Empty message="Tidak ada data concurrent untuk filter/periode ini." icon={FileText} />
              )}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Dataset <strong style={{ color: 'var(--text-secondary)' }}>{result.dataset}</strong> · {result.total} baris
                {result.total > PREVIEW_ROW_LIMIT && ` (preview ${PREVIEW_ROW_LIMIT} pertama — export tetap pakai semua baris)`}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {FORMATS.map(f => {
                  const isChartPdf = f.value === 'pdf' && ['histogram', 'concurrent'].includes(result.dataset);
                  return (
                    <Button key={f.value} size="sm" variant={f.value === result.defaultFormat ? 'primary' : 'ghost'}
                      loading={exportingFormat === f.value} onClick={() => handleExport(f.value)}>
                      <Download size={12} /> {isChartPdf ? 'PDF (chart)' : f.label}
                    </Button>
                  );
                })}
              </div>
            </div>

            {result.rows?.length ? (
              <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', maxHeight: 440, overflow: 'auto' }}>
                <Table columns={previewColumns} data={result.rows.slice(0, PREVIEW_ROW_LIMIT)} emptyMessage="Tidak ada data" />
              </div>
            ) : (
              <Empty message="Tidak ada data untuk filter/periode ini." icon={FileText} />
            )}
          </div>
        ))}
      </Modal>

      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Hapus Template" width={380}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 20 }}>
          Yakin hapus template <strong>{deleteConfirm?.name}</strong>?
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>Batal</Button>
          <Button variant="danger" onClick={handleDelete}>Hapus</Button>
        </div>
      </Modal>
    </div>
  );
}